import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, CheckCircle, Code, Play, BarChart3, Target, Book, ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

export const Documentation: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (code: string, label: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(label);
      toast({
        title: "Copied to clipboard",
        description: `${label} copied successfully`,
      });
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <Code className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            🐍 AlphaLoop Python Client
          </h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          A comprehensive Python library for interacting with the AlphaLoop Experiments API
        </p>
        
        {/* Quick Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          <Button variant="outline" onClick={() => document.getElementById('installation')?.scrollIntoView({ behavior: 'smooth' })}>
            🚀 Quick Install
          </Button>
          <Button variant="outline" onClick={() => document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth' })}>
            💡 Examples
          </Button>
          <Button variant="outline" onClick={() => document.getElementById('api-reference')?.scrollIntoView({ behavior: 'smooth' })}>
            📚 API Reference
          </Button>
        </div>
      </div>

      {/* Quick Installation Box */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6 text-center">
        <h3 className="text-xl font-bold mb-4">🚀 Quick Installation</h3>
        <div className="bg-black/20 rounded-lg p-4 font-mono text-sm mb-4 flex items-center justify-between">
          <code>pip install --index-url https://app.runalphaloops.com/public/ alphaloop-experiments</code>
          <Button
            size="sm"
            variant="outline"
            onClick={() => copyToClipboard("pip install --index-url https://app.runalphaloops.com/public/ alphaloop-experiments", "Install command")}
            className="ml-2 bg-white/20 border-white/30 text-white hover:bg-white/30"
          >
            {copiedCode === "Install command" ? (
              <CheckCircle className="h-3 w-3" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        </div>
        <p className="text-blue-100">
          Then set your API key: <code className="bg-black/20 px-2 py-1 rounded">export ALPHALOOP_API_KEY="ak_your_key_here"</code>
        </p>
      </div>

      {/* Table of Contents */}
      <div className="bg-card border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Book className="h-5 w-5" />
          📋 Table of Contents
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            { id: 'installation', title: 'Installation & Setup' },
            { id: 'quick-start', title: 'Quick Start' },
            { id: 'examples', title: 'Usage Examples' },
            { id: 'api-reference', title: 'API Reference' },
            { id: 'error-handling', title: 'Error Handling' },
            { id: 'support', title: 'Support & Troubleshooting' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
              className="text-left text-primary hover:text-primary/80 hover:underline p-2 rounded transition-colors"
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>

      {/* Installation Section */}
      <InstallationSection copyToClipboard={copyToClipboard} copiedCode={copiedCode} />

      {/* Quick Start Section */}
      <QuickStartSection copyToClipboard={copyToClipboard} copiedCode={copiedCode} />

      {/* Examples Section */}
      <ExamplesSection copyToClipboard={copyToClipboard} copiedCode={copiedCode} />

      {/* API Reference Section */}
      <APIReferenceSection />

      {/* Error Handling Section */}
      <ErrorHandlingSection copyToClipboard={copyToClipboard} copiedCode={copiedCode} />

      {/* Support Section */}
      <SupportSection copyToClipboard={copyToClipboard} copiedCode={copiedCode} />
    </div>
  );
};

// Code Block Component
const CodeBlock: React.FC<{
  title: string;
  code: string;
  language?: string;
  icon?: any;
  copyToClipboard: (code: string, label: string) => void;
  copiedCode: string | null;
}> = ({ title, code, language = 'python', icon: Icon = Code, copyToClipboard, copiedCode }) => (
  <div className="bg-card border rounded-lg overflow-hidden">
    <div className="flex items-center justify-between bg-muted px-4 py-3 border-b">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <h3 className="font-medium">{title}</h3>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => copyToClipboard(code, title)}
        className="flex items-center gap-1"
      >
        {copiedCode === title ? (
          <CheckCircle className="h-3 w-3 text-green-600" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
        {copiedCode === title ? 'Copied!' : 'Copy'}
      </Button>
    </div>
    <pre className="p-4 text-sm overflow-x-auto bg-slate-950 text-slate-100">
      <code>{code}</code>
    </pre>
  </div>
);

// Installation Section Component
const InstallationSection: React.FC<{
  copyToClipboard: (code: string, label: string) => void;
  copiedCode: string | null;
}> = ({ copyToClipboard, copiedCode }) => (
  <section id="installation" className="space-y-6">
    <h2 className="text-3xl font-bold text-primary border-b pb-3">🛠 Installation & Setup</h2>
    
    <div className="space-y-4">
      <h3 className="text-xl font-semibold">Prerequisites</h3>
      <ul className="list-disc list-inside space-y-2 text-muted-foreground">
        <li>Python 3.8 or higher</li>
        <li>An AlphaLoop account with API access</li>
        <li>Valid API key (get one from Settings → API Keys)</li>
      </ul>
    </div>

    <CodeBlock
      title="Install from AlphaLoop Package Index (Recommended)"
      code="pip install --index-url https://app.runalphaloops.com/public/ alphaloop-experiments"
      language="bash"
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <CodeBlock
      title="Setup Your API Key"
      code={`# Set environment variable (recommended)
export ALPHALOOP_API_KEY="ak_your_actual_key_here"

# Or add to your shell profile (.bashrc, .zshrc, etc.)
echo 'export ALPHALOOP_API_KEY="ak_your_actual_key_here"' >> ~/.bashrc`}
      language="bash"
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <p className="text-green-800">
        <strong>✅ Verify Installation:</strong><br />
        Run <code className="bg-green-100 px-2 py-1 rounded">python -c "import alphaloop_experiments; print('✅ Installed successfully!')"</code>
      </p>
    </div>
  </section>
);

// Quick Start Section Component  
const QuickStartSection: React.FC<{
  copyToClipboard: (code: string, label: string) => void;
  copiedCode: string | null;
}> = ({ copyToClipboard, copiedCode }) => (
  <section id="quick-start" className="space-y-6">
    <h2 className="text-3xl font-bold text-primary border-b pb-3">🚀 Quick Start</h2>
    
    <CodeBlock
      title="Basic Usage"
      code={`from alphaloop_experiments import ExperimentsClient

# Initialize the client
client = ExperimentsClient(api_key="ak_your_api_key_here")

# Create and run a simple experiment
experiment = client.run_simple_experiment(
    name="My First Experiment",
    parameters={
        "model_type": "random_forest",
        "n_estimators": 100,
        "max_depth": 10,
        "dataset": "customer_churn"
    },
    result_data={
        "accuracy": 0.95,
        "precision": 0.92,
        "recall": 0.94,
        "f1_score": 0.93
    }
)

print(f"✅ Experiment created: {experiment.external_id}")
print(f"📊 Status: {experiment.status.value}")
print(f"📈 Results: {len(experiment.results)}")`}
      icon={Play}
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
      {[
        { icon: '🚀', title: 'Simple API', desc: 'Easy-to-use interface for experiment management' },
        { icon: '🔐', title: 'Secure Authentication', desc: 'API key-based authentication with automatic header management' },
        { icon: '📊', title: 'Rich Data Models', desc: 'Type-safe experiment and result objects' },
        { icon: '⚡', title: 'Rate Limiting', desc: 'Built-in rate limit handling and retries' },
        { icon: '🛡️', title: 'Error Handling', desc: 'Comprehensive exception handling with descriptive error messages' },
        { icon: '📈', title: 'Result Tracking', desc: 'Support for interim, partial, and final results' },
      ].map((feature) => (
        <div key={feature.title} className="bg-card border rounded-lg p-4">
          <h4 className="font-semibold text-primary mb-2">
            {feature.icon} {feature.title}
          </h4>
          <p className="text-sm text-muted-foreground">{feature.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

// Examples Section Component
const ExamplesSection: React.FC<{
  copyToClipboard: (code: string, label: string) => void;
  copiedCode: string | null;
}> = ({ copyToClipboard, copiedCode }) => (
  <section id="examples" className="space-y-6">
    <h2 className="text-3xl font-bold text-primary border-b pb-3">💡 Usage Examples</h2>
    
    <CodeBlock
      title="Machine Learning Pipeline"
      code={`import time
from alphaloop_experiments import ExperimentsClient, ResultType

client = ExperimentsClient(api_key="ak_your_key")

# Start experiment for model training
experiment = client.run_experiment(
    name="Customer Churn Prediction",
    parameters={
        "model_type": "xgboost",
        "dataset": "customer_data_2024",
        "features": ["tenure", "monthly_charges", "total_charges"],
        "target": "churn",
        "test_size": 0.2,
        "random_state": 42
    }
)

# Track training progress with interim results
for epoch in range(1, 6):
    time.sleep(1)  # Simulate training time
    
    client.add_result(
        experiment.external_id,
        result_data={
            "epoch": epoch,
            "train_accuracy": 0.7 + (epoch * 0.03),
            "val_accuracy": 0.65 + (epoch * 0.025),
            "train_loss": 0.5 - (epoch * 0.08),
            "val_loss": 0.55 - (epoch * 0.07)
        },
        result_type=ResultType.INTERIM
    )

# Add final results
final_result = client.add_final_result(
    experiment.external_id,
    result_data={
        "final_train_accuracy": 0.87,
        "final_val_accuracy": 0.84,
        "final_test_accuracy": 0.82,
        "precision": 0.81,
        "recall": 0.79,
        "f1_score": 0.80,
        "roc_auc": 0.85,
        "training_time_seconds": 245,
        "model_size_mb": 12.5
    }
)

print("🎉 Training completed!")
print(f"📊 Final accuracy: {final_result.result_data['final_test_accuracy']}")`}
      icon={BarChart3}
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <CodeBlock
      title="A/B Testing"
      code={`# Test different model configurations
configurations = [
    {"model": "random_forest", "n_estimators": 100, "max_depth": 10},
    {"model": "random_forest", "n_estimators": 200, "max_depth": 15},
    {"model": "xgboost", "n_estimators": 100, "learning_rate": 0.1},
]

results = []

for i, config in enumerate(configurations):
    experiment = client.run_experiment(
        name=f"A/B Test Config {i+1}",
        parameters=config
    )
    
    # Simulate model training and evaluation
    accuracy = 0.75 + (i * 0.05)  # Mock results
    
    client.add_final_result(
        experiment.external_id,
        result_data={"accuracy": accuracy, "config_id": i+1}
    )
    
    results.append((experiment.external_id, accuracy))

# Find best performing configuration
best_experiment_id, best_accuracy = max(results, key=lambda x: x[1])
print(f"🏆 Best experiment: {best_experiment_id} with accuracy: {best_accuracy}")`}
      icon={Target}
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />
  </section>
);

// API Reference Section Component
const APIReferenceSection: React.FC = () => (
  <section id="api-reference" className="space-y-6">
    <h2 className="text-3xl font-bold text-primary border-b pb-3">📚 API Reference</h2>
    
    <div className="space-y-4">
      <h3 className="text-xl font-semibold">ExperimentsClient</h3>
      
      <div className="bg-card border rounded-lg p-4">
        <h4 className="font-semibold mb-2">Initialization</h4>
        <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
          <code>{`client = ExperimentsClient(
    api_key="ak_your_api_key_here",
    base_url="https://app.runalphaloops.com",  # Optional
    timeout=30,  # Optional
    user_id="your_user_id"  # Optional, extracted from API key if not provided
)`}</code>
        </pre>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <h4 className="font-semibold mb-3">Experiment Management</h4>
          <ul className="space-y-1 text-sm">
            <li><code className="text-xs">create_experiment()</code> - Create new experiment</li>
            <li><code className="text-xs">get_experiment()</code> - Retrieve experiment by ID</li>
            <li><code className="text-xs">update_experiment()</code> - Update experiment</li>
            <li><code className="text-xs">complete_experiment()</code> - Mark as completed</li>
            <li><code className="text-xs">cancel_experiment()</code> - Cancel experiment</li>
          </ul>
        </div>

        <div className="bg-card border rounded-lg p-4">
          <h4 className="font-semibold mb-3">Result Management</h4>
          <ul className="space-y-1 text-sm">
            <li><code className="text-xs">add_result()</code> - Add result</li>
            <li><code className="text-xs">add_final_result()</code> - Add final result</li>
            <li><code className="text-xs">run_experiment()</code> - Quick experiment creation</li>
            <li><code className="text-xs">run_simple_experiment()</code> - Create, run, and complete</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Error Handling Section Component
const ErrorHandlingSection: React.FC<{
  copyToClipboard: (code: string, label: string) => void;
  copiedCode: string | null;
}> = ({ copyToClipboard, copiedCode }) => (
  <section id="error-handling" className="space-y-6">
    <h2 className="text-3xl font-bold text-primary border-b pb-3">🛡️ Error Handling</h2>
    
    <CodeBlock
      title="Exception Handling"
      code={`from alphaloop_experiments import (
    AlphaLoopAPIError,
    AuthenticationError,
    NotFoundError, 
    RateLimitError,
    ValidationError,
    ExperimentExistsError
)

try:
    experiment = client.create_experiment(experiment_data)
except ExperimentExistsError as e:
    print(f"Experiment already exists: {e.external_id}")
except ValidationError as e:
    print(f"Invalid data: {e.message}")
except RateLimitError as e:
    print(f"Rate limited. Try again at: {e.reset_at}")
except AuthenticationError as e:
    print(f"Auth failed: {e.message}")
except NotFoundError as e:
    print(f"Not found: {e.message}")
except AlphaLoopAPIError as e:
    print(f"API error: {e.message}")`}
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-semibold text-amber-800 mb-2">⚠️ Authentication Errors</h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• Check your API key is correct</li>
          <li>• Ensure the key hasn't been deleted</li>
          <li>• Verify the environment variable is set correctly</li>
        </ul>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-semibold text-amber-800 mb-2">⚠️ Rate Limit Errors</h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• The client handles rate limits automatically</li>
          <li>• If you consistently hit limits, slow down your requests</li>
          <li>• Check your rate limit quota in the dashboard</li>
        </ul>
      </div>
    </div>
  </section>
);

// Support Section Component
const SupportSection: React.FC<{
  copyToClipboard: (code: string, label: string) => void;
  copiedCode: string | null;
}> = ({ copyToClipboard, copiedCode }) => (
  <section id="support" className="space-y-6">
    <h2 className="text-3xl font-bold text-primary border-b pb-3">🔧 Support & Troubleshooting</h2>
    
    <CodeBlock
      title="Debug Mode"
      code={`import logging
logging.basicConfig(level=logging.DEBUG)

client = ExperimentsClient(api_key="ak_your_key_here")`}
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <CodeBlock
      title="Test Installation"
      code={`from alphaloop_experiments import ExperimentsClient

try:
    client = ExperimentsClient(api_key="ak_your_key_here")
    
    # Test with a simple experiment
    exp = client.run_simple_experiment(
        name="Config Test",
        parameters={"test": True},
        result_data={"status": "ok"}
    )
    print("✅ Configuration is working!")
    
except Exception as e:
    print(f"❌ Configuration error: {e}")`}
      copyToClipboard={copyToClipboard}
      copiedCode={copiedCode}
    />

    <div className="bg-card border rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Support Channels</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span>📧</span>
            <strong>Email:</strong>
            <span className="text-muted-foreground">support@alphaloop.com</span>
          </div>
          <div className="flex items-center gap-2">
            <span>🔑</span>
            <strong>API Keys:</strong>
            <Button variant="link" className="p-0 h-auto text-primary">
              Settings → API Keys
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span>🏠</span>
            <strong>Dashboard:</strong>
            <Button variant="link" className="p-0 h-auto text-primary">
              Main Dashboard
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            <p><strong>Version:</strong> 1.0.0</p>
            <p><strong>Last updated:</strong> December 2024</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Export reusable components for use in other parts of the app
export { CodeBlock, InstallationSection, QuickStartSection, ExamplesSection }; 